import { textStyleLabel, type BlockStyle, type FlowOptions, type Node, type StyleGroup, type Theme, type Spacing } from "@broadpaper/core";
import { Field, NumberInput, Segmented, Select, Switch, Section, Button } from "../ui/primitives.js";
import { ColorField } from "./fields/ColorField.js";
import { fontOptions } from "./font-options.js";

interface Props {
  node: Node;
  groups: StyleGroup[];
  theme: Theme;
  textStyles: string[];
  setStyle: (patch: BlockStyle | null) => void;
  setFlow: (patch: FlowOptions) => void;
  allowOverrides: boolean;
}

const SIDES = ["top", "right", "bottom", "left"] as const;

function SpacingGrid({ value, onChange }: { value: Spacing | undefined; onChange: (v: Spacing | undefined) => void }) {
  const v = value ?? {};
  return (
    <div>
      <div className="bpe-spacing-grid">
        {SIDES.map((s) => (
          <NumberInput key={s} value={v[s] ?? null} onChange={(n) => onChange({ ...v, [s]: n ?? undefined })} min={-200} max={400} placeholder="0" aria-label={s} />
        ))}
      </div>
      <div className="bpe-spacing-grid">
        {SIDES.map((s) => (
          <div key={s} className="bpe-spacing-grid__lbl">{s}</div>
        ))}
      </div>
    </div>
  );
}

/** The built-in style groups shown for every block (per its `styleGroups`). */
export function StyleGroups({ node, groups, theme, textStyles, setStyle, setFlow, allowOverrides }: Props) {
  const s = node.style ?? {};
  const f = node.flow ?? {};
  const has = (g: StyleGroup) => groups.includes(g);
  const hasOverrides = Object.keys(s).length > 0;

  return (
    <>
      {allowOverrides && (has("textStyle") || has("typography") || has("alignment")) ? (
        <Section title="Typography" actions={hasOverrides ? <Button size="sm" variant="ghost" icon="rotate-ccw" tooltip="Reset style overrides" onClick={() => setStyle(null)} /> : null}>
          <div className="bpe-prop-grid">
            {has("textStyle") ? (
              <Field label="Text style">
                <Select value={s.textStyle ?? ""} onChange={(v) => setStyle({ textStyle: v || undefined })} placeholder="Block default" options={textStyles.map((t) => ({ value: t, label: textStyleLabel(t) }))} />
              </Field>
            ) : null}
            {has("typography") ? (
              <>
                <Field label="Font">
                  <Select value={s.fontFamily ?? ""} onChange={(v) => setStyle({ fontFamily: v || undefined })} placeholder="Inherit" options={fontOptions(theme, s.fontFamily)} />
                </Field>
                <Field label="Size"><NumberInput value={s.fontSize ?? null} onChange={(v) => setStyle({ fontSize: v ?? undefined })} min={4} max={200} unit="px" placeholder="inherit" /></Field>
                <Field label="Weight">
                  <Select value={s.fontWeight === undefined ? "" : String(s.fontWeight)} onChange={(v) => setStyle({ fontWeight: v ? Number(v) : undefined })} placeholder="Inherit" options={[300, 400, 500, 600, 700, 800].map((w) => ({ value: String(w), label: w === 400 ? "Regular" : w === 700 ? "Bold" : String(w) }))} />
                </Field>
                <Field label="Line height"><NumberInput value={s.lineHeight ?? null} onChange={(v) => setStyle({ lineHeight: v ?? undefined })} min={0.6} max={4} step={0.05} placeholder="inherit" /></Field>
                <Field label="Spacing"><NumberInput value={s.letterSpacing ?? null} onChange={(v) => setStyle({ letterSpacing: v ?? undefined })} min={-0.2} max={1} step={0.01} unit="em" placeholder="0" /></Field>
                <Field label="Case">
                  <Segmented value={s.textTransform ?? "none"} onChange={(v) => setStyle({ textTransform: v === "none" ? undefined : (v as BlockStyle["textTransform"]) })} options={[{ value: "none", label: "Aa" }, { value: "uppercase", label: "AA" }, { value: "lowercase", label: "aa" }, { value: "capitalize", label: "Aa Bb" }]} />
                </Field>
                <Field label="Style">
                  <Segmented value={s.fontStyle ?? "normal"} onChange={(v) => setStyle({ fontStyle: v === "normal" ? undefined : "italic" })} options={[{ value: "normal", label: "Normal" }, { value: "italic", label: "Italic" }]} />
                </Field>
                <Field label="Colour"><ColorField value={s.color} onChange={(v) => setStyle({ color: v })} theme={theme} allowTransparent={false} placeholder="Inherit" /></Field>
              </>
            ) : null}
            {has("alignment") ? (
              <Field label="Align">
                <Segmented value={s.textAlign ?? "left"} onChange={(v) => setStyle({ textAlign: v === "left" ? undefined : (v as BlockStyle["textAlign"]) })} iconOnly ariaLabel="Text alignment" options={[{ value: "left", label: "Left", icon: "align-left" }, { value: "center", label: "Centre", icon: "align-center" }, { value: "right", label: "Right", icon: "align-right" }, { value: "justify", label: "Justify", icon: "align-justify" }]} />
              </Field>
            ) : null}
          </div>
        </Section>
      ) : null}

      {allowOverrides && (has("spacing") || has("size")) ? (
        <Section title="Spacing & size" defaultOpen={false}>
          <div className="bpe-prop-grid">
            {has("spacing") ? (
              <>
                <Field label="Margin" stacked><SpacingGrid value={s.margin} onChange={(v) => setStyle({ margin: v })} /></Field>
                <Field label="Padding" stacked><SpacingGrid value={s.padding} onChange={(v) => setStyle({ padding: v })} /></Field>
              </>
            ) : null}
            {has("size") ? (
              <>
                <Field label="Width"><NumberInput value={typeof s.width === "number" ? s.width : null} onChange={(v) => setStyle({ width: v ?? undefined })} min={8} max={2000} unit="px" placeholder="auto" /></Field>
                <Field label="Placement">
                  <Segmented value={s.align ?? "left"} onChange={(v) => setStyle({ align: v === "left" ? undefined : (v as BlockStyle["align"]) })} options={[{ value: "left", label: "Left" }, { value: "center", label: "Centre" }, { value: "right", label: "Right" }]} />
                </Field>
              </>
            ) : null}
          </div>
        </Section>
      ) : null}

      {allowOverrides && (has("background") || has("border")) ? (
        <Section title="Background & border" defaultOpen={false}>
          <div className="bpe-prop-grid">
            {has("background") ? <Field label="Background"><ColorField value={s.background} onChange={(v) => setStyle({ background: v })} theme={theme} placeholder="None" /></Field> : null}
            {has("border") ? (
              <>
                <Field label="Border"><NumberInput value={s.border?.width ?? null} onChange={(v) => setStyle({ border: { ...(s.border ?? {}), width: v ?? undefined } })} min={0} max={20} unit="px" placeholder="0" /></Field>
                <Field label="Border colour"><ColorField value={s.border?.color} onChange={(v) => setStyle({ border: { ...(s.border ?? {}), color: v } })} theme={theme} placeholder="Theme border" /></Field>
                <Field label="Line"><Segmented value={s.border?.style ?? "solid"} onChange={(v) => setStyle({ border: { ...(s.border ?? {}), style: v as "solid" | "dashed" | "dotted" } })} options={[{ value: "solid", label: "Solid" }, { value: "dashed", label: "Dashed" }, { value: "dotted", label: "Dotted" }]} /></Field>
                <Field label="Radius"><NumberInput value={s.border?.radius ?? null} onChange={(v) => setStyle({ border: { ...(s.border ?? {}), radius: v ?? undefined } })} min={0} max={100} unit="px" placeholder="0" /></Field>
                <Field label="Sides">
                  <div style={{ display: "flex", gap: 4 }}>
                    {SIDES.map((side) => {
                      const sides = s.border?.sides ?? [...SIDES];
                      const on = sides.includes(side);
                      return (
                        <Button key={side} size="sm" active={on} onClick={() => setStyle({ border: { ...(s.border ?? {}), sides: on ? sides.filter((x) => x !== side) : [...sides, side] } })} style={{ flex: 1, textTransform: "capitalize" }}>
                          {side[0]!.toUpperCase()}
                        </Button>
                      );
                    })}
                  </div>
                </Field>
              </>
            ) : null}
            <Field label="Opacity"><NumberInput value={s.opacity ?? null} onChange={(v) => setStyle({ opacity: v ?? undefined })} min={0} max={1} step={0.05} placeholder="1" /></Field>
          </div>
        </Section>
      ) : null}

      {has("flow") ? (
        <Section title="Page flow" defaultOpen={false} icon="file-output">
          <div className="bpe-prop-grid">
            <Field label="Keep together" hint="Never split this block across pages (unless it is taller than a page).">
              <Switch checked={!!f.keepTogether} onChange={(v) => setFlow({ keepTogether: v || undefined })} />
            </Field>
            <Field label="Keep with next" hint="Stay on the same page as the start of the next block.">
              <Switch checked={!!f.keepWithNext} onChange={(v) => setFlow({ keepWithNext: v || undefined })} />
            </Field>
            <Field label="Break before">
              <Segmented value={f.breakBefore ?? "auto"} onChange={(v) => setFlow({ breakBefore: v === "auto" ? undefined : "page" })} options={[{ value: "auto", label: "Auto" }, { value: "page", label: "New page" }]} />
            </Field>
            <Field label="Break after">
              <Segmented value={f.breakAfter ?? "auto"} onChange={(v) => setFlow({ breakAfter: v === "auto" ? undefined : "page" })} options={[{ value: "auto", label: "Auto" }, { value: "page", label: "New page" }]} />
            </Field>
            <Field label="Min. lines before"><NumberInput value={f.minOrphans ?? null} onChange={(v) => setFlow({ minOrphans: v ?? undefined })} min={1} max={10} placeholder="2" /></Field>
            <Field label="Min. lines after"><NumberInput value={f.minWidows ?? null} onChange={(v) => setFlow({ minWidows: v ?? undefined })} min={1} max={10} placeholder="2" /></Field>
          </div>
        </Section>
      ) : null}
    </>
  );
}
